<script setup>
import { ref, onMounted, getCurrentInstance, inject, computed } from "vue";
import { useRouter, RouterLink } from "vue-router";
import adminJoinStateService from "../../../services/jobfair/adminJoinStateService";
import SNBAdmin from "../../../../../layouts/nav/SNBAdmin.vue";
import { usePageLoadingStore } from "../../../../../stores/pageLoading";
import { useMatchingUpdateStore } from "../../../../user/stores/matching/matchingUpdateStore";
import _ from "lodash";
import KSASecondaryButton from "../../../../../components/ui-components/button/KSASecondaryButton.vue";
import KSAPrimaryButton from "../../../../../components/ui-components/button/KSAPrimaryButton.vue";

const router = useRouter();

const loadingStore = usePageLoadingStore();
const matchingUpdateStore = useMatchingUpdateStore();
const { $pageListBoardNo, $toast, $gbNameKR, $number, $yyyymmdd, $ojfStateGb } = getCurrentInstance().appContext.config.globalProperties;

const ojfStateGb = ref($ojfStateGb); // 행사 진행 상태

onMounted(() => {
    retrieveList(); // 행사 리스트 조회
});

const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(0);
const totalCount = ref(0);
const searchOpen = ref(true);

const joinStateList = ref([]); // 행사 리스트

// 초기화 버튼 처리를 위한 ref 지정
const openYnListRef = ref(null);
const ojfStateGbListRef = ref(null);

// 검색 조건
const searchInput = ref({
    openYnList: ["Y", "N"],
    ojfStateGbList: ["00", "01", "02", "03", "04"],
    searchOjfName: ""
});

const GroupOpenYn = ref([
    { label: "공개", value: "Y" },
    { label: "비공개", value: "N" }
]);

const pageSizeGroup = ref([
    { label: "10개씩 보기", value: "10" },
    { label: "30개씩 보기", value: "30" },
    { label: "50개씩 보기", value: "50" },
    { label: "100개씩 보기", value: "100" }
]);

const retrieveList = async () => {
    let paramObj = {
        searchOpenYn: _.map(searchInput.value.openYnList).join(","),
        searchOjfStateGb: _.map(searchInput.value.ojfStateGbList).join(","),
        searchOjfName: searchInput.value.searchOjfName,
        pageNumber: currentPage.value,
        size: pageSize.value
    };

    loadingStore.loading = true;

    adminJoinStateService.retrieveOnlineJobFairCountListPaging(paramObj)
        .then(async (res) => {
            if (res.data.code.toLowerCase() === "success") {
                const result = res.data.result;
                totalPages.value = result.totalPages;
                totalCount.value = result.totalElements;
                joinStateList.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
            }
            loadingStore.loading = false;
        }).catch((res) => {
            loadingStore.loading = false;
            $toast("error", res);
    });
};

// 검색 - '초기화' 버튼 클릭
const clearSearchInput = () => {
    searchInput.value = {
        openYnList: ["Y", "N"],
        ojfStateGbList: ["00", "01", "02", "03", "04"],
        searchOjfName: ""
    };
    openYnListRef.value.initOptions();
    ojfStateGbListRef.value.initOptions();
};

// 검색 - '검색' 버튼 click
const onClickSearch = () => {
    currentPage.value = 1;
    retrieveList();
};

// 페이지 선택
const onSelectPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveList();
};

// 페이지 사이즈 변경.
const onSelectPageSize = (option) => {
    pageSize.value = option.value;
    currentPage.value = 1;
    retrieveList();
};
</script>

<template>
    <div class="content">
        <div class="sub-title">
            <h3>신청 현황</h3>
        </div>
        <!-- s: 검색 -->
        <div class="filter-wrap" :class="{'open': searchOpen}">
            <p @click="searchOpen = !searchOpen">검색</p>
            <div class="filter">
                <dl>
                    <dt style="display:block;"><span>공개 상태</span><br /><small style="font-weight: normal;">복수 선택
                        가능</small></dt>
                    <dd>
                        <KSACheckBoxGroup
                            ref="openYnListRef"
                            className="mr-md"
                            name="openYnList"
                            v-model="searchInput.openYnList"
                            :options="GroupOpenYn" />
                    </dd>
                </dl>
                <dl>
                    <dt style="display:block;"><span>진행 상태</span><br /><small style="font-weight: normal;">복수 선택
                        가능</small></dt>
                    <dd>
                        <KSACheckBoxGroup
                            ref="ojfStateGbListRef"
                            className="mr-md"
                            name="ojfStateGbList"
                            v-model="searchInput.ojfStateGbList"
                            :options="ojfStateGb" />
                    </dd>
                </dl>
                <dl>
                    <dt>행사명</dt>
                    <dd>
                        <KSAInput class="text-box" v-model="searchInput.searchOjfName"
                                    placeholder="행사명을 입력해 주세요." />
                    </dd>
                </dl>
                <div class="btn-wrap">
                    <KSASecondaryButton class="gray_button" text="초기화" @click="clearSearchInput" />
                    <KSAPrimaryButton text="검색" @click="onClickSearch" />
                </div>
            </div>
        </div>
        <!-- e: 검색 -->
        <!-- s: 버튼영역 -->
        <div class="list-top">
            <p>전체 <span>{{ $number(totalCount) }}</span>건</p>
            <div>
                <KSASelect :options="pageSizeGroup" class="select" @selectOption="onSelectPageSize" />
            </div>
        </div>
        <!-- e: 버튼영역 -->
        <div class="table-form">
            <table class="horizontal" v-if="joinStateList !== null">
                <tr>
                    <th rowspan="2">NO</th>
                    <th rowspan="2">행사ID</th>
                    <th rowspan="2">행사구분</th>
                    <th rowspan="2">공개 상태</th>
                    <th rowspan="2">진행 상태</th>
                    <th rowspan="2">행사명</th>
                    <th rowspan="2">접수기간</th>
                    <th rowspan="2" class="border-right">행사기간</th>
                    <th colspan="4">재직자/기업</th>
                    <th colspan="4" class="th-red">학생/취업준비생</th>
                </tr>
                <tr>
                    <th>신청</th>
                    <th>승인</th>
                    <th>반려</th>
                    <th>취소</th>
                    <th class="th-red">신청</th>
                    <th class="th-red">승인</th>
                    <th class="th-red">반려</th>
                    <th class="th-red">취소</th>
                </tr>
                <tr v-for="ojf in joinStateList">
                    <td>{{ ojf.boardNo }}</td>
                    <td>{{ ojf.ojfId }}</td>
                    <td>
                        <span v-if="ojf.ojfGb === '01'">기업공채관</span>
                        <span v-else>채용박람회</span>
                    </td>
                    <td>
                        <span v-if="ojf.openYn === 'Y'">공개</span>
                        <span v-else>비공개</span>
                    </td>
                    <td>
                        <!-- 상태가 '03'일 때 -->
                        <span v-if="ojf.ojfStateGb === '03'" class="blue">
                            {{ $gbNameKR("OJF_STATE_GB", ojf.ojfStateGb) }}
                            {{ $isInRegPeriod(ojf.regStartYmd, ojf.regEndYmd) ? '(신청 접수중)' : ($isAfterRegEnd(ojf.regEndYmd) ? '(신청 마감)' : '') }}
                        </span>
                        <!-- 상태가 '03'이 아닐 때 -->
                        <span v-else :class="ojf.ojfStateGb === '05' ? 'red' : ojf.ojfStateGb === '04' ? 'gray' : ''">
                            {{ $gbNameKR("OJF_STATE_GB", ojf.ojfStateGb) }}
                        </span>
                    </td>
                    <td class="clickable blue">
                        <RouterLink :to="{name:'JoinStateDetail', params:{id: ojf.ojfId}, query:{ojfGb: ojf.ojfGb}}">{{ ojf.ojfName }}</RouterLink>
                    </td>
                    <td>{{ $yyyymmdd(ojf.regStartYmd) }} ~ {{ $yyyymmdd(ojf.regEndYmd) }}</td>
                    <td>{{ $yyyymmdd(ojf.ojfStartYmd) }} ~ {{ $yyyymmdd(ojf.ojfEndYmd) }}</td>

                    <td>{{ Number(ojf.corpJoinState01) + Number(ojf.corpJoinState02) }}</td>
                    <td>{{ ojf.corpJoinState11 }}</td>
                    <td>{{ ojf.corpJoinState21 }}</td>
                    <td>{{ Number(ojf.corpJoinState03) + Number(ojf.corpJoinState04) }}</td>

                    <td>{{ Number(ojf.joinState01) + Number(ojf.joinState02) }}</td>
                    <td>{{ ojf.joinState11 }}</td>
                    <td>{{ ojf.joinState21 }}</td>
                    <td>{{ Number(ojf.joinState03) + Number(ojf.joinState04) }}</td>
                </tr>
            </table>
            <div class="result-none" v-if="joinStateList.length === 0">
                등록된 행사가 없습니다.
            </div>
        </div>
        <Paginate
            v-if="joinStateList.length > 0"
            v-model="currentPage"
            :page-count="totalPages"
            :click-handler="onSelectPage"
        />
    </div>
</template>

<style lang="scss" scoped>
@import "../../../../../assets/scss/theme";
.blue {
  color: $blue_color;
}
.red {
  color: $red_color;
}
.gray {
  color: $gray_color;
}

.table-form {
  table {
    &.vertical {
      &.align-left {
        th {
          text-align: left;
          span {
            display: block;
            font-size: $font-size_xs;
            color: $gray_color;
            font-weight: normal;
          }
        }
      }
    }
  }
  .clickable {
    text-decoration: underline;
    cursor: pointer;
  }
}

</style>
